import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const AuthenticationCallbackHandler = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying your authentication...');

  useEffect(() => {
    let subscription = null;
    let timeoutId = null;

    const handleCallback = async () => {
      try {
        // First, check if there's already an active session
        const { data: { session: existingSession } } = await supabase?.auth?.getSession();

        if (existingSession?.user) {
          setStatus('success');
          setMessage('Authentication successful! Redirecting to dashboard...');
          timeoutId = setTimeout(() => {
            navigate('/fleet-operations-dashboard', { replace: true });
          }, 1500);
          return;
        }

        // Listen for auth state changes (handles magic link token exchange)
        const { data: { subscription: sub } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_IN' && session?.user) {
            setStatus('success');
            setMessage('Authentication successful! Redirecting to dashboard...');
            timeoutId = setTimeout(() => {
              navigate('/fleet-operations-dashboard', { replace: true });
            }, 1500);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            setStatus('success');
            setMessage('Authentication successful! Redirecting to dashboard...');
            timeoutId = setTimeout(() => {
              navigate('/fleet-operations-dashboard', { replace: true });
            }, 1500);
          }
        });

        subscription = sub;

        // Fallback timeout: if no auth event fires within 10 seconds, show error
        timeoutId = setTimeout(() => {
          setStatus('error');
          setMessage('No active session found. Please try logging in again.');
          setTimeout(() => {
            navigate('/admin-authentication-portal', {
              replace: true,
              state: { error: 'Authentication timed out. Please try again.' }
            });
          }, 3000);
        }, 10000);

      } catch (error) {
        console.error('Authentication callback error:', error);
        setStatus('error');
        setMessage(error?.message || 'Authentication failed. Please try again.');
        timeoutId = setTimeout(() => {
          navigate('/admin-authentication-portal', {
            replace: true,
            state: { error: error?.message || 'Authentication failed' }
          });
        }, 3000);
      }
    };

    handleCallback();

    return () => {
      subscription?.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-8">
          <div className="flex flex-col items-center space-y-6">
            {/* RPM Diesel Branding */}
            <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
              {status === 'processing' && (
                <Icon 
                  name="Loader2" 
                  size={32} 
                  color="var(--color-primary)" 
                  strokeWidth={2}
                  className="animate-spin"
                />
              )}
              {status === 'success' && (
                <Icon 
                  name="CheckCircle2" 
                  size={32} 
                  color="var(--color-success)" 
                  strokeWidth={2}
                />
              )}
              {status === 'error' && (
                <Icon 
                  name="AlertCircle" 
                  size={32} 
                  color="var(--color-destructive)" 
                  strokeWidth={2}
                />
              )}
            </div>

            {/* Status Heading */}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {status === 'processing' && 'Authenticating...'}
                {status === 'success' && 'Success!'}
                {status === 'error' && 'Authentication Failed'}
              </h1>
              <p className="text-muted-foreground">{message}</p>
            </div>

            {/* Progress Indicators */}
            {status === 'processing' && (
              <div className="w-full space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  <span className="text-sm text-muted-foreground">Processing authentication tokens...</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary/60 rounded-full animate-pulse delay-100"></div>
                  <span className="text-sm text-muted-foreground">Establishing secure session...</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-pulse delay-200"></div>
                  <span className="text-sm text-muted-foreground">Validating credentials...</span>
                </div>
              </div>
            )}

            {/* SSL Security Indicator */}
            <div className="flex items-center space-x-2 pt-4 border-t border-border w-full justify-center">
              <Icon 
                name="Lock" 
                size={16} 
                color="var(--color-success)" 
                strokeWidth={2} 
              />
              <span className="text-xs text-success font-medium">SSL Secured Connection</span>
            </div>

            {/* Error Details */}
            {status === 'error' && (
              <div className="w-full pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  Redirecting to login page in 3 seconds...
                </p>
              </div>
            )}

            {/* Success Animation */}
            {status === 'success' && (
              <div className="w-full pt-4 border-t border-border">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-8 h-1 bg-success rounded-full animate-pulse"></div>
                  <div className="w-8 h-1 bg-success rounded-full animate-pulse delay-75"></div>
                  <div className="w-8 h-1 bg-success rounded-full animate-pulse delay-150"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Support Information */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground mb-2">
            RPM Diesel Fleet Manager © {new Date()?.getFullYear()}
          </p>
          {status === 'error' && (
            <p className="text-xs text-muted-foreground">
              Need help? Contact support at support@rpmdiesel.com
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthenticationCallbackHandler;